// Lucka 2: Samlad slutdokumentation till förvaltning – ett paket med innehållsförteckning.
import JSZip from "jszip";
import {
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  WidthType,
} from "docx";
import { supabase } from "@/integrations/supabase/client";
import { collectProjectBundle, rowsToCsv } from "./projectBundle";
import { trackUsage } from "@/lib/usage/track";

export interface HandoverEntry {
  docNumber: string;
  title: string;
  owner: string;
  count: number;
  ok: boolean;
  detail: string;
}

const CONTENTS: { docNumber: string; title: string; owner: string; tables: string[] }[] = [
  { docNumber: "01", title: "Rutin systematiskt arbetsmiljöarbete", owner: "Bas-P", tables: ["sam_routines"] },
  { docNumber: "03", title: "SKR-bedömning", owner: "Bas-P", tables: ["skr_assessments"] },
  { docNumber: "04", title: "Förhandsanmälan", owner: "Byggherre", tables: ["prior_notifications"] },
  { docNumber: "05", title: "Uppdragsbekräftelse Bas-P och Bas-U", owner: "Byggherre", tables: ["handover_documents"] },
  { docNumber: "08", title: "Farligt material", owner: "Bas-P", tables: ["hazmat_surveys", "hazmat_items"] },
  { docNumber: "10", title: "Arbetsområdesutredning", owner: "Bas-P", tables: ["work_area_surveys"] },
  { docNumber: "12", title: "Arbetsmiljöplan", owner: "Bas-P/Bas-U", tables: ["amp_sections", "work_environment_plans"] },
  { docNumber: "15", title: "Skydds- och ordningsregler", owner: "Bas-U", tables: ["safety_rule_documents"] },
  { docNumber: "24", title: "Arbetsberedningar", owner: "Entreprenör", tables: ["work_preparations"] },
  { docNumber: "27", title: "Skyddsronder", owner: "Bas-U", tables: ["safety_rounds"] },
  { docNumber: "28", title: "Tillbud och olyckor", owner: "Bas-U", tables: ["incident_reports"] },
  { docNumber: "31", title: "Risker i bruksskedet", owner: "Bas-P", tables: ["residual_use_risks", "folder_risk_assessments"] },
  { docNumber: "33", title: "Uppföljning bruksskede", owner: "Byggherre", tables: ["use_phase_followups", "use_phase_followup_items"] },
  { docNumber: "22", title: "Överlämning till byggherre och förvaltning", owner: "Bas-U", tables: ["handover_documents"] },
];

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const safeName = (value: string | null | undefined, fallback: string) =>
  (value || fallback).replace(/[^\p{L}\p{N}_-]+/gu, "_").slice(0, 60);

type Bundle = Awaited<ReturnType<typeof collectProjectBundle>>;

export const buildHandoverEntries = (bundle: Bundle): HandoverEntry[] =>
  CONTENTS.map((c) => {
    const count = c.tables.reduce((sum, t) => sum + (bundle.tables[t]?.length ?? 0), 0);
    return {
      docNumber: c.docNumber,
      title: c.title,
      owner: c.owner,
      count,
      ok: count > 0,
      detail: count > 0 ? `${count} poster` : "saknas",
    };
  });

export const collectHandoverStatus = async (projectId: string) => {
  const bundle = await collectProjectBundle(projectId);
  const entries = buildHandoverEntries(bundle);
  const done = entries.filter((e) => e.ok).length;
  return {
    bundle,
    entries,
    done,
    total: entries.length,
    percent: Math.round((done / entries.length) * 100),
  };
};

const buildTocDocument = (bundle: Bundle, entries: HandoverEntry[]) => {
  const rows = [
    new TableRow({
      children: ["Dok", "Handling", "Ansvarig", "Status"].map(
        (h) =>
          new TableCell({
            width: { size: 2340, type: WidthType.DXA },
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })],
          })
      ),
    }),
    ...entries.map(
      (e) =>
        new TableRow({
          children: [e.docNumber, e.title, e.owner, e.ok ? `Ingår (${e.count})` : "Saknas"].map(
            (v) =>
              new TableCell({
                width: { size: 2340, type: WidthType.DXA },
                margins: { top: 80, bottom: 80, left: 120, right: 120 },
                children: [new Paragraph({ children: [new TextRun({ text: String(v) })] })],
              })
          ),
        })
    ),
  ];

  return new Document({
    styles: { default: { document: { run: { font: "Arial", size: 22 } } } },
    sections: [
      {
        children: [
          new Paragraph({
            heading: HeadingLevel.HEADING_1,
            children: [new TextRun({ text: "Slutdokumentation arbetsmiljö", bold: true, size: 32 })],
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: `Projekt: ${bundle.meta.projectName ?? bundle.meta.projectId}. Sammanställd ${new Date().toLocaleDateString("sv-SE")}.`,
              }),
            ],
          }),
          new Paragraph({ text: "" }),
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [new TextRun({ text: "Innehållsförteckning", bold: true, size: 26 })],
          }),
          new Table({ width: { size: 9360, type: WidthType.DXA }, columnWidths: [2340, 2340, 2340, 2340], rows }),
          new Paragraph({ text: "" }),
          new Paragraph({
            children: [
              new TextRun({
                text: "Underlagen finns i mappen underlag som CSV samt i projekt.json i maskinläsbar form. Handlingar markerade som saknas ska kompletteras innan slutlig överlämning till förvaltningen.",
              }),
            ],
          }),
        ],
      },
    ],
  });
};

export const downloadFinalDocumentation = async (projectId: string): Promise<void> => {
  const { bundle, entries } = await collectHandoverStatus(projectId);
  const zip = new JSZip();

  const docBlob = await Packer.toBlob(buildTocDocument(bundle, entries));
  zip.file("Slutdokumentation_innehallsforteckning.docx", docBlob);

  const folder = zip.folder("underlag");
  Object.entries(bundle.tables).forEach(([table, rows]) => {
    if (!rows.length) return;
    folder?.file(`${table}.csv`, rowsToCsv(rows));
  });
  zip.file("projekt.json", JSON.stringify(bundle, null, 2));

  const { data: exportsData } = await supabase
    .from("document_exports")
    .select("document_type, document_name, version, filename, verification_code, created_at")
    .eq("project_id", projectId)
    .order("created_at", { ascending: false });

  if (exportsData?.length) {
    zip.file("exporterade_dokument.csv", rowsToCsv(exportsData as Record<string, unknown>[]));
  }

  const blob = await zip.generateAsync({ type: "blob" });
  triggerDownload(
    blob,
    `Slutdokumentation_${safeName(bundle.meta.projectName, projectId)}_${new Date().toISOString().slice(0, 10)}.zip`
  );

  void trackUsage("final_documentation_created", { projectId });
};
